import {
  ButtonVariant,
  ButtonWithTooltipActionProps,
  KeyboardShortcut,
  openConfirmDialog,
  openDeleteDialog,
} from 'platform/components';
import {match} from 'ts-pattern';

import {isEmpty, isNil, isNotNil, last} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {HeaderActionType, HeaderDynamicActionsResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {buildArray, Nullish} from 'shared/web';

import type {EntityHeaderAction} from '../components/EntityHeader/types/EntityHeaderAction';

type HeaderActionCallback = (key: string, type?: HeaderActionType) => void | Promise<unknown>;

type CreateHeaderActionsConfig = {
  actions: HeaderDynamicActionsResponse['actions'] | Nullish;
  callback: HeaderActionCallback;
  isLoading?: boolean;
  isDisabled?: boolean;
  keyboardShortcuts?: Partial<Record<string, KeyboardShortcut>>;
};

const DEFAULT_ACTION_TYPE: HeaderActionType = 'BE';

/**
 * Destructive actions always open a confirmation dialog first; the page
 * callback runs only after the user confirms. Returning the mutation promise
 * from the callback keeps the dialog open with a loading confirm button until
 * the request settles.
 *
 * Because the dialog guards execution, destructive actions may own the default
 * Ctrl/⌘+Enter chord like any other primary button — the chord can only ever
 * open the dialog, never execute the action directly. While the dialog is open
 * the header button is behind it, so the shortcut registry refuses to re-fire
 * it (getIsElementInTopDialog).
 *
 * The confirm button is focused on open so the keyboard flow is
 * chord → Enter to confirm (or Esc/Tab+Enter to cancel).
 */
const DESTRUCTIVE_ACTION_CONFIRMATIONS: Record<
  string,
  (onConfirm: () => void | Promise<unknown>) => void
> = {
  delete: (onConfirm) => openDeleteDialog({onConfirm, shouldFocusConfirmButton: true}),
  discard: (onConfirm) => openConfirmDialog({onConfirm, shouldFocusConfirmButton: true}),
};

// Object.hasOwn guards the backend-supplied key against resolving to
// Object.prototype members (e.g. 'toString')
const isDestructiveActionKey = (key: string) =>
  Object.hasOwn(DESTRUCTIVE_ACTION_CONFIRMATIONS, key);

const invokeWithDestructiveConfirmation = (
  callback: HeaderActionCallback,
  key: string,
  type: HeaderActionType
) => {
  if (!isDestructiveActionKey(key)) {
    return callback(key, type);
  }

  DESTRUCTIVE_ACTION_CONFIRMATIONS[key](() => callback(key, type));
};

/**
 * Destructive primary buttons default to the danger variant so their risk is
 * visible at a glance; an explicit backend-provided variant always wins.
 */
const getPrimaryButtonVariant = (key: string, variant: string | undefined): ButtonVariant => {
  if (isNotNil(variant)) {
    return variant as ButtonVariant;
  }

  return isDestructiveActionKey(key) ? 'danger' : 'primary';
};

/**
 * Picks which primary action receives the default Ctrl/⌘+Enter chord.
 * A keyboard chord is a singleton per page, so only one button can own it:
 * the last rendered (rightmost) primary button — the main CTA. Destructive
 * buttons are eligible too: their chord opens the confirmation dialog, so a
 * reflexive chord press can never delete or discard an entity directly.
 */
const getDefaultShortcutActionKey = (
  primaryActions: {key: string}[],
  explicit: Partial<Record<string, KeyboardShortcut>> | Nullish
): string | undefined => {
  const hasExplicitPrimaryShortcut = primaryActions.some(
    (action) => explicit?.[action.key] === KeyboardShortcut.ModEnter
  );
  if (hasExplicitPrimaryShortcut) {
    return undefined;
  }

  return last(primaryActions)?.key;
};

/**
 * Explicit config always wins per key — a default never overwrites caller intent:
 * - key present in the caller map (even as `undefined`) → caller value (opt-out supported)
 * - key absent → ModEnter when this action is the page's default shortcut owner
 */
const resolveKeyboardShortcut = (
  actionKey: string,
  defaultShortcutActionKey: string | undefined,
  explicit: Partial<Record<string, KeyboardShortcut>> | Nullish
): KeyboardShortcut | undefined => {
  if (isNotNil(explicit) && actionKey in explicit) {
    return explicit[actionKey];
  }

  return actionKey === defaultShortcutActionKey ? KeyboardShortcut.ModEnter : undefined;
};

/**
 * Creates header actions based on the provided configuration.
 * Actions are organized into three categories:
 * 1. Primary action buttons (from optionClick)
 * 2. Context menu actions (from remaining non-printout options)
 * 3. Printout actions (separate dropdown for type === 'PRINTOUT')
 *
 * @param {CreateHeaderActionsConfig} props - The configuration object for creating header actions.
 * @param {Object} props.actions - Actions configuration.
 * @param {Array<Object>} props.actions.options - List of action options available for the header.
 * @param {string|string[]} [props.actions.optionClick] - Key or array of keys representing primary actions to display as buttons.
 * @param {boolean} [props.isLoading] - Indicates whether the actions are in a loading state.
 * @param {Function} props.callback - Callback function invoked when an action is clicked. Receives key and type parameters.
 *
 * @param {Object} [props.keyboardShortcuts] - Optional map of action key to platform KeyboardShortcut.
 *   By default the last (rightmost, main CTA) primary button gets Ctrl/⌘+Enter. For destructive
 *   actions the chord opens the confirmation dialog rather than executing the action.
 *   A key present in this map always wins over the default — pass `{<key>: undefined}` to opt a
 *   page out. An explicit ModEnter assignment suppresses the default only when its action is
 *   rendered as a primary button. Hidden, missing, and menu-only actions cannot register the
 *   shortcut, so their assignments leave the default available to the last primary button.
 *   Note this guarantees single ownership only among buttons created by this util; a ModEnter
 *   button mounted elsewhere (e.g. a basket modal) overlaps via the platform LIFO registry —
 *   runtime resolution is correct, but dev builds log a duplicate-registration console.error.
 *
 * @returns {EntityHeaderAction[]} An array of header actions consisting of:
 *   - Context menu dropdown (icon button with remaining non-printout actions, excluding hidden ones)
 *   - Printout dropdown (button with 'Generate Documents' label for printout actions)
 *   - Primary action buttons (derived from optionClick, excluding hidden ones, rendered last)
 *
 * @example
 * Example usage with all action types:
 * const config = {
 *   actions: {
 *     options: [
 *       { key: 'edit', title: 'Edit', variant: 'primary', position: 1, type: 'BE' },
 *       { key: 'delete', title: 'Delete', variant: 'secondary', position: 2, type: 'BE' },
 *       { key: 'invoice', title: 'Invoice', position: 3, type: 'PRINTOUT' },
 *       { key: 'receipt', title: 'Receipt', position: 4, type: 'PRINTOUT' },
 *       { key: 'share', title: 'Share', style: 'hidden', position: 5, type: 'BE' },
 *     ],
 *     optionClick: ['edit'], // Primary button(s)
 *   },
 *   isLoading: false,
 *   callback: (key, type) => {
 *     console.log(`Action clicked: ${key}, Type: ${type}`);
 *   },
 * };
 *
 * const headerActions = createHeaderActions(config);
 * // Result: [context menu dropdown, printout dropdown, edit button]
 */
export const createHeaderActions = (props: CreateHeaderActionsConfig): EntityHeaderAction[] => {
  if (isNil(props.actions) || isEmpty(props.actions.options)) {
    return [];
  }

  const optionClickKeys = match(props.actions.optionClick)
    .when(Array.isArray, (keys) => keys)
    .when(String, (key) => [key])
    .otherwise(() => []);

  const primaryActions = optionClickKeys
    .map((key) => props.actions?.options.find((option) => option.key === key))
    .filter((action): action is NonNullable<typeof action> => Boolean(action))
    .filter((action) => action.style !== 'hidden');

  const defaultShortcutActionKey = getDefaultShortcutActionKey(
    primaryActions,
    props.keyboardShortcuts
  );

  const buttonsWithTooltip = primaryActions.map(
    (action): ButtonWithTooltipActionProps => ({
      type: 'buttonWithTooltip',
      title: action.title ?? action.key ?? '',
      variant: getPrimaryButtonVariant(action.key, action.variant),
      isDisabled:
        action.key === 'discard'
          ? action.style === 'disabled'
          : action.style === 'disabled' || props.isDisabled === true,
      isLoading: props.isLoading ?? false,
      keyboardShortcut: resolveKeyboardShortcut(
        action.key,
        defaultShortcutActionKey,
        props.keyboardShortcuts
      ),
      tooltip: {
        label: action.tooltip?.label,
        description: action.tooltip?.description,
      },
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        invokeWithDestructiveConfirmation(
          props.callback,
          action.key,
          action.type ?? DEFAULT_ACTION_TYPE
        );
      },
    })
  );

  const contextOptions = props.actions.options
    .filter((option) => !optionClickKeys.includes(option.key))
    .filter((option) => option.type !== 'PRINTOUT')
    .filter((option) => option.style !== 'hidden')
    .sort((a, b) => a.position - b.position);

  const printoutOptions = props.actions.options
    .filter((option) => !optionClickKeys.includes(option.key))
    .filter((option) => option.type === 'PRINTOUT')
    .filter((option) => option.style !== 'hidden')
    .sort((a, b) => a.position - b.position);

  return buildArray<EntityHeaderAction>()
    .when(isNotNilOrEmpty(contextOptions), {
      type: 'dropdown-iconButton',
      icon: 'navigation/more_vert',
      priority: 'secondary',
      menuItems: contextOptions.map((contextOption) => ({
        variant: 'primary',
        isDisabled:
          contextOption.key === 'discard'
            ? contextOption.style === 'disabled'
            : contextOption.style === 'disabled' || props.isDisabled === true,
        isLoading: props.isLoading ?? false,
        label: contextOption.title ?? contextOption.key,
        tooltip: {
          label: contextOption.tooltip?.label,
          description: contextOption.tooltip?.description,
        },
        onClick: (event) => {
          event.stopPropagation();
          invokeWithDestructiveConfirmation(
            props.callback,
            contextOption.key,
            contextOption.type ?? DEFAULT_ACTION_TYPE
          );
        },
      })),
    })
    .when(isNotNilOrEmpty(printoutOptions), {
      type: 'dropdown-button',
      title: i18n.t('generals.labels.generateDocuments'),
      rightIcon: 'navigation/expand_more',
      variant: 'secondary',
      menuItems: printoutOptions.map((contextOption) => ({
        variant: 'primary',
        isDisabled:
          contextOption.key === 'discard'
            ? contextOption.style === 'disabled'
            : contextOption.style === 'disabled' || props.isDisabled === true,
        isLoading: props.isLoading ?? false,
        label: contextOption.title ?? contextOption.key,
        tooltip: {
          label: contextOption.tooltip?.label,
          description: contextOption.tooltip?.description,
        },
        onClick: (event) => {
          event.stopPropagation();
          invokeWithDestructiveConfirmation(
            props.callback,
            contextOption.key,
            contextOption.type ?? DEFAULT_ACTION_TYPE
          );
        },
      })),
    })
    .concat(buttonsWithTooltip);
};
